import { getWeighInFromStartAt } from '@utils/date-utils';
import { Database } from './database.types';

// Use this type to prettify the types that are unions of other types or interfaces
// EXAMPLE: type NiceReadableType = PrettifyType<SomeUnionType>
export type PrettifyType<T> = {
    [K in keyof T]: T[K];
} & unknown;

export enum BIRTHDAY_CUTOFF {
    NONE = 'none',
    JAN_1 = 'jan-1',
    EVENT_START = 'event-start',
}

export enum DIVISION_LIMITATION {
    NONE = 'none',
    AGE = 'age',
    GRADE = 'grade',
}

export enum DIVISION_STYLE {
    MADISON_SYSTEM = 'madison system',
    FIXED_WEIGHT_CLASSES = 'fixed weight classes',
}

export enum EVENT_ROLE {
    OWNER = 'owner',
    STAFF = 'staff',
    PAYMENT_ADMIN = 'deprecated_do_not_use',
    PARTICIPANT = 'participant',
    VIEWER = 'viewer',
}

export enum PAYMENT_STATUS {
    CANCELLED = 'cancelled',
    PAID = 'paid',
    PENDING = 'pending', // checkout session created but not "submitted" yet, we're waiting for user to complete the payment
    PROCESSING = 'processing', // checkout session submitted, but payment not yet confirmed
    ERRORED = 'errored',
    UNPAID = 'unpaid',
}

export enum TEAM_ROLE {
    OWNER = 'owner',
    COACH = 'coach',
    ATHLETE = 'athlete',
    PARENT = 'parent',
}

export enum STATUS {
    INVITED = 'invited', // requires user approval
    ACTIVE = 'active',
    DECLINED = 'declined',
    LEFT = 'left',
    REMOVED = 'removed',
    CANCELLED = 'cancelled',
    PENDING = 'pending', // Requires admin approval
}

export const COACH_ROLE = [TEAM_ROLE.OWNER, TEAM_ROLE.COACH];
export const ALL_TEAM_ROLES = Object.values(TEAM_ROLE);

export const TEAM_ROLE_OPTIONS: { label: string; value: TEAM_ROLE }[] = [
    { value: TEAM_ROLE.OWNER, label: 'Team Admin' },
    { value: TEAM_ROLE.COACH, label: 'Coach' },
    { value: TEAM_ROLE.ATHLETE, label: 'Athlete' },
    { value: TEAM_ROLE.PARENT, label: 'Parent' },
];

export const MALE = 'male';
export const FEMALE = 'female';

export const ALL_GENDER_OPTIONS = [
    { value: MALE, label: 'Male' },
    { value: FEMALE, label: 'Female' },
];

export const ALL_GRADE_OPTIONS = [
    { value: 'K', label: 'Kindergarten' },
    { value: '1', label: '1st Grade' },
    { value: '2', label: '2nd Grade' },
    { value: '3', label: '3rd Grade' },
    { value: '4', label: '4th Grade' },
    { value: '5', label: '5th Grade' },
    { value: '6', label: '6th Grade' },
    { value: '7', label: '7th Grade' },
    { value: '8', label: '8th Grade' },
    { value: '9', label: '9th Grade' },
    { value: '10', label: '10th Grade' },
    { value: '11', label: '11th Grade' },
    { value: '12', label: '12th Grade' },
];

export const getBirthdayCutOffOptions = (startAt: string | undefined | Date) => {
    const jan1stLabel = `Age on Jan 1st, ${
        startAt
            ? new Date(startAt).getMonth() > 6
                ? new Date(startAt).getFullYear() + 1
                : new Date(startAt).getFullYear()
            : new Date().getFullYear()
    }`;

    return [
        { label: 'Age on day of event', value: BIRTHDAY_CUTOFF.EVENT_START },
        { label: jan1stLabel, value: BIRTHDAY_CUTOFF.JAN_1 },
    ];
};

type TeamPure = Database['public']['Tables']['teams']['Row'];
type DbUserPure = Database['public']['Tables']['users']['Row'];
type PaymentInfoPure = Database['public']['Tables']['payments']['Row'];
type StripeInfoPure = Database['public']['Tables']['stripe']['Row'];
type UserTeamPure = Database['public']['Tables']['user_team']['Row'];
type DbEventPure = Database['public']['Tables']['events']['Row'];
type EventMembershipPure = Database['public']['Tables']['user_event']['Row'];
type DbAthletePure = Database['public']['Tables']['athletes']['Row'];
type DbDivisionPure = Database['public']['Tables']['divisions']['Row'];

export type DbStripeEvent = Database['public']['Tables']['stripe_webhooks']['Row'];

// NOTE: class.maxWeight needs to be a string to accommodate units like "Kg" or "Lbs"
export type Class = Database['public']['Tables']['classes']['Row'];
export type UserAthleteRelationship = Database['public']['Tables']['user_athlete']['Row'];

export interface User extends DbUser {
    email: string;
}
export interface Division extends DbDivision {
    classes: Class[];
}
export interface EventPure extends DbEvent {
    divisions: Division[];
    weighInAt: WeighInItem[] | null; // technically it can be any JSON, but we want to enforce this type when possible
    registrantsCount: number;
}

interface TeamAthletePure extends DbTeamAthlete {
    athleteCreatedAt: string | null;
    athleteUpdatedAt: string | null;
    birthday: string | null;
    codeOfConduct: string;
    description: string;
    email: string | null;
    firstName: string;
    gender: string | null;
    teamImageUrl: string;
    fee: number;
    lastName: string;
    membershipId: string;
    name: string;
    phone: string;
    role: TEAM_ROLE;
    teamCreatedAt: string;
    teamCreatedByUserId: string;
    teamId: string;
    teamPayToUserId: string;
    teamStatus: STATUS;
    teamUpdatedAt: string;
    userCreatedAt: string;
    userId: string;
    userImageUrl: string;
    userStatus: STATUS;
    userUpdatedAt: string;
    weight: number | null;
}
interface AthleteTeamMemberPure
    extends Athlete,
        Omit<
            TeamAthletePure,
            | 'athleteCreatedAt'
            | 'athleteUpdatedAt'
            | 'gender'
            | 'weight'
            | 'birthday'
            | 'id'
            | 'grade'
            | 'activeSince'
        > {
    status: STATUS;
}

// Export prettified types
export type AthleteTeamMember = PrettifyType<AthleteTeamMemberPure>;
export type PaymentInfo = PrettifyType<PaymentInfoPure>;
export type StripeInfo = PrettifyType<StripeInfoPure>;
export type TeamAthlete = PrettifyType<TeamAthletePure>;
export type Team = PrettifyType<TeamPure>;
export type DbUser = PrettifyType<DbUserPure>;
export type UserTeam = PrettifyType<UserTeamPure>;
export type DbEvent = PrettifyType<DbEventPure>;
export type Event = PrettifyType<EventPure>;
export type EventMembership = PrettifyType<EventMembershipPure>;
export type DbAthlete = PrettifyType<DbAthletePure>;
export type DbDivision = PrettifyType<DbDivisionPure>;
export type Athlete = PrettifyType<DbAthlete & Omit<User, 'email'> & { email: string | null }>; // athletes don't always have emails

// overriding types for TeamMember because postgres' erroneous null typing
// https://github.com/orgs/supabase/discussions/14151#discussioncomment-7162821
type DbTeamAthlete = Database['public']['Views']['TeamAthletes']['Row'];
type DbRegisteredUser = Database['public']['Views']['RegisteredUsers']['Row'];
type DbEventRoleStatus = Database['public']['Views']['EventRoles']['Row'];
type DbUserAthlete = Database['public']['Views']['UserAthlete']['Row'];
type DbRegisteredEvents = Database['public']['Views']['RegisteredEvents']['Row'];
type DbRelatedAthlete = Database['public']['Views']['RelatedAthletes']['Row'];

export interface RegisteredEvent extends DbRegisteredEvents {
    athleteId: string;
    birthdayCutoff: BIRTHDAY_CUTOFF;
    createdAt: string;
    createdByUserId: string;
    description: string;
    eventId: string;
    eventRole: EVENT_ROLE;
    eventStatus: STATUS;
    fee: number;
    id: string;
    imageUrl: string;
    isPrivate: boolean;
    location: string;
    maxEntries: number | null;
    name: string;
    paymentStatus: PAYMENT_STATUS;
    payToUserId: string;
    registrantsCount: number;
    relationship: string;
    relationStatus: STATUS;
    startAt: string;
    status: STATUS;
    updatedAt: string;
    userEventStatus: STATUS;
    userId: string;
    weighInAt: WeighInItem[];
}
export interface RelatedAthlete extends DbRelatedAthlete {
    athleteId: string;
    birthday: string;
    createdAt: string;
    email: string | null;
    firstName: string;
    gender: string;
    imageUrl: string | null;
    lastName: string;
    phone: string | null;
    relationship: string;
    relationStatus: STATUS;
    userId: string;
    userStatus: STATUS;
    weight: number;
}
export interface UserAthlete extends DbUserAthlete {
    parentOrUserId: string; //
    athleteId: string; // child or athlete
    birthday: string;
    createdAt: string;
    updatedAt: string;
    email: string;
    firstName: string;
    lastName: string;
    phone: string;
    gender: string;
    imageUrl: string;
    weight: number;
    parentStatus: STATUS;
    userStatus: STATUS;
    grade: string;
    activeSince: string;
}
interface EventRoleStatusPure extends DbEventRoleStatus {
    // has all keys from event with same names except waiver, divisions,
    userId: string;
    id: string; // event id
    birthdayCutoff: Database['public']['Enums']['birthdayCutoff'];
    createdAt: string;
    createdByUserId: string;
    description: string;
    fee: number;
    imageUrl: string;
    isPrivate: boolean;
    location: string;
    maxEntries: number | null;
    name: string;
    payToUserId: string;
    placeId: string | null;
    registrantsCount: number;
    startAt: string;
    status: Database['public']['Enums']['status'];
    updatedAt: string;
    // waiver: string;
    weighInAt: WeighInItem[] | null;
    weightTolerance: number | null;
}
export type EventRoleStatus = PrettifyType<EventRoleStatusPure>;

export interface RegisteredUser extends DbRegisteredUser {
    id: string; // authId
    userId: string;
    email: string; // authEmail
}

export interface EditUserForm {
    firstName: string;
    lastName: string;
    email: string;
}
export interface EditCoachForm extends EditUserForm {
    role: TEAM_ROLE;
    status: STATUS;
}
export interface NewAthlete
    extends EditUserForm,
        Pick<UserAthlete, 'gender' | 'weight' | 'birthday' | 'grade' | 'activeSince'> {}

export interface InviteUserToTeamBody
    extends EditUserForm,
        Partial<Omit<NewAthlete, 'email' | 'firstName' | 'lastName'>> {
    role: TEAM_ROLE;
    teamId: string;
    teamStatus: STATUS;
    userId?: string;
}

export interface InviteUserToEventBody
    extends EditUserForm,
        Partial<Omit<NewAthlete, 'email' | 'firstName' | 'lastName'>> {
    role: EVENT_ROLE;
    eventId: string;
    membershipStatus: STATUS;
    userId?: string; // if email & no userId we'll initiate a new user
}

// ClassToInsert is a type that is used to create a new Weight Class in the database
export type ClassToInsert = Omit<Class, 'id' | 'createdAt' | 'updatedAt'> &
    Partial<Pick<Class, 'divisionId'>>;

// DivisionToInsert is a type that is used to create a new division in the database
export type DivisionToInsert = Omit<
    Division,
    'id' | 'classes' | 'createdAt' | 'updatedAt' | 'eventId'
> & {
    classes: ClassToInsert[];
};

export const GET_NULL_CLASS = (currentClassesLength = 0): ClassToInsert => ({
    name: '',
    divisionId: '',
    maxWeight: '',
    maxEntries: null,
    sortIndex: currentClassesLength,
    status: STATUS.ACTIVE,
});

export const GET_NULL_DIVISION = (
    currentDivisionsLength = 0,
    defaultBracketStyle: DIVISION_STYLE | null,
): DivisionToInsert => ({
    name: '',
    bracketStyle: defaultBracketStyle as unknown as DIVISION_STYLE, // default to invalid value -> useful for forms
    classes: defaultBracketStyle === DIVISION_STYLE.MADISON_SYSTEM ? [] : [GET_NULL_CLASS()],
    entryLimitation: null as unknown as DIVISION_LIMITATION, // default to invalid value -> useful for forms
    minAge: null,
    maxAge: null,
    sortIndex: currentDivisionsLength,
    status: STATUS.ACTIVE,
});

export interface WeighInItem extends Record<string, string> {
    location: string;
    placeId: string;
    note: string;
    weighInAtDateTime: string;
}

export const GET_DEFAULT_WEIGH_IN = (
    startAt: Date | string | number,
    location: string,
    placeId: string | null,
): WeighInItem => ({
    location: location || '',
    placeId: placeId || '',
    note: '',
    weighInAtDateTime: getWeighInFromStartAt(startAt),
});
